import { useEffect, useState } from 'react';
import { newsList } from '@/mocks/ranking';

const navLinks = [
  { href: '#hotlist', label: '实时热榜', icon: 'ri-fire-line' },
  { href: '#pk', label: '人气PK', icon: 'ri-vip-crown-line' },
  { href: '#absurd', label: '离谱投票', icon: 'ri-emotion-laugh-line' },
  { href: '#news', label: '瓜点速递', icon: 'ri-newspaper-line' },
  { href: '#faq', label: '吃瓜问答', icon: 'ri-questionnaire-line' },
  { href: '#about', label: '关于我们', icon: 'ri-team-line' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const doSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    window.dispatchEvent(
      new CustomEvent('chigua:search', { detail: { query: q } }),
    );
    document.getElementById('news')?.scrollIntoView({ behavior: 'smooth' });
    setSearchOpen(false);
    setQuery('');
  };

  const quickHot = (q: string) => {
    setSearchOpen(true);
    setQuery(q);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors ${
        scrolled ? 'border-b border-background-200/70 bg-background-50/90 backdrop-blur' : 'bg-background-50'
      }`}
    >
      <nav className="mx-auto flex w-full items-center justify-between gap-4 px-4 py-3 md:px-6 lg:px-10">
        {/* Logo */}
        <a href="#top" className="group flex items-center gap-2.5">
          <span className="relative flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-background-50">
            <i className="ri-vip-crown-fill text-xl leading-none" />
          </span>
          <span className="leading-tight">
            <span className="block font-display text-xl text-foreground-950 md:text-2xl">
              吃瓜<span className="text-primary-600">榜单</span>
            </span>
            <span className="hidden text-[11px] text-foreground-500 sm:block">今日又有什么瓜？</span>
          </span>
        </a>

        {/* Desktop links */}
        <ul className="hidden items-center gap-1 lg:flex">
          {navLinks.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
              >
                <i className={`${l.icon} text-base leading-none`} />
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <div className="relative hidden md:block">
            {searchOpen ? (
              <form
                onSubmit={doSearch}
                className="flex items-center gap-1 rounded-full border border-background-300 bg-background-100 py-1 pl-3 pr-1 focus-within:border-secondary-400"
              >
                <i className="ri-search-line text-sm text-foreground-500" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="搜索榜单 / 吃瓜话题"
                  className="w-40 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
                />
                <button
                  type="submit"
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-500 text-background-50 transition-colors hover:bg-secondary-600"
                  aria-label="搜索"
                >
                  <i className="ri-arrow-right-line text-sm leading-none" />
                </button>
              </form>
            ) : (
              <button
                onClick={() => setSearchOpen(true)}
                className="flex h-10 w-10 items-center justify-center rounded-full text-foreground-600 transition-colors hover:bg-background-100"
                aria-label="打开搜索"
              >
                <i className="ri-search-line text-lg leading-none" />
              </button>
            )}
          </div>

          <a
            href="#hotlist"
            className="hidden whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-all hover:bg-primary-600 sm:inline-flex sm:items-center sm:gap-1.5"
          >
            <i className="ri-fire-fill text-base leading-none" />
            去投票
          </a>

          <button
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-foreground-700 hover:bg-background-100 lg:hidden"
            aria-label="切换菜单"
          >
            <i className={`${open ? 'ri-close-line' : 'ri-menu-line'} text-xl leading-none`} />
          </button>
        </div>
      </nav>

      {/* Quick hot keywords */}
      <div className="hidden items-center justify-center gap-2 px-4 pb-2 text-xs md:flex lg:hidden">
        {['顶流舞台', '塌房考古', '萌宠顶流'].map((k) => (
          <button
            key={k}
            onClick={() => quickHot(k)}
            className="whitespace-nowrap rounded-full bg-accent-100 px-3 py-1 text-accent-800 transition-colors hover:bg-accent-200"
          >
            {k}
          </button>
        ))}
      </div>

      {/* Mobile panel */}
      {open && (
        <div className="border-t border-background-200/70 bg-background-50 lg:hidden">
          <div className="space-y-1 px-4 py-3">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-secondary-100"
              >
                <i className={`${l.icon} text-lg leading-none text-secondary-600`} />
                {l.label}
              </a>
            ))}
            <div className="border-t border-background-200 pt-3">
              <form onSubmit={doSearch} className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2">
                <i className="ri-search-line text-base text-foreground-500" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="搜索榜单 / 吃瓜话题"
                  className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
                />
              </form>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}